import MuiAppBar from '@mui/material/AppBar';
import ButtonBase from '@mui/material/ButtonBase';
import MuiCard, { type CardTypeMap } from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import MuiDivider from '@mui/material/Divider';
import type { OverridableComponent } from '@mui/material/OverridableComponent';
import { styled } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { ChevronDown } from 'lucide-react';
import { type ButtonHTMLAttributes, type ElementType, type HTMLAttributes, type ReactNode, type Ref, useId } from 'react';

import { cn } from '@/lib/cn';
import type { CardLook, SurfaceLook, SurfaceTone } from '@/theme/looks';
import { ICON_SIZE } from './iconSize';
import { useOpenState } from './overlayState';

export type { SurfaceTone };

const SurfaceRoot = styled('div', { name: 'KitSurface', slot: 'root' })<{ ownerState: SurfaceLook }>({});

const CONTROLS = new Set(['button', 'a', 'label']);

export interface SurfaceProps extends HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
  /**
   * The fill: none, the card surface, the page, a quiet grey, the solid brand colour, the creative's
   * white paper, the media scrim, see-through glass, or a tint of one colour role.
   */
  tone?: SurfaceTone;
  outline?: SurfaceLook['outline'];
  radius?: SurfaceLook['radius'];
  /** Tints on hover, for a surface that is a control. `quiet` hovers grey, not brand. */
  interactive?: boolean | 'quiet';
  /** Gives the brand tint and border of a chosen option. */
  selected?: boolean;
  /** Gives a drop shadow, from `sm` (low) to `xl` (high). */
  elevation?: SurfaceLook['elevation'];
  /** Makes the whole block see-through: `some` a little, `half` by half. */
  dimmed?: Exclude<SurfaceLook['dimmed'], 'none'>;
  /** The element to use, such as `button` or `label`. */
  as?: ElementType;
  type?: string;
  disabled?: boolean;
}

/** Shows a block with a kit fill, border and corner. Layout comes from `className`.
 *  A button, link, label or clickable block gets the pointer. */
export function Surface(
  {
    tone = 'surface',
    outline = 'none',
    radius = 'md',
    interactive = false,
    selected = false,
    elevation = 'none',
    dimmed,
    as = 'div',
    ref,
    ...props
  }: SurfaceProps,
) {
  const look: SurfaceLook = {
    tone,
    outline,
    radius,
    interactive: interactive === true ? 'brand' : interactive || 'none',
    selected,
    clickable: (typeof as === 'string' && CONTROLS.has(as)) || Boolean(props.onClick),
    elevation,
    dimmed: dimmed ?? 'none',
  };
  return <SurfaceRoot ref={ref} as={as} ownerState={look} {...props} />;
}

/** The kit card, with its selected glow from the `KitCard` theme variants. */
const CardRoot = styled(MuiCard, { name: 'KitCard', slot: 'root' })({}) as OverridableComponent<
  CardTypeMap<{ ownerState: CardLook }>
>;

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Selected/active treatment: brand-tinted border and glow. */
  glow?: boolean;
  /** Moves the card up and gives it a `md` or `lg` shadow on hover. */
  lift?: Exclude<CardLook['lift'], 'none'>;
}

/** Shows a kit card with no outer margin. The parent sets the gap. */
export function Card({ glow = false, lift, ...props }: CardProps) {
  return <CardRoot ownerState={{ glow, lift: lift ?? 'none' }} {...props} />;
}

export interface SectionCardProps {
  title?: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  className?: string;
  bodyClassName?: string;
}

/** Shows a card with an optional title, description and actions above its body. */
export function SectionCard({
  title,
  description,
  action,
  children,
  flush = false,
  className,
  bodyClassName,
}: SectionCardProps) {
  const hasHeader = title || description || action;

  return (
    <Card className={cn('flex flex-col', className)}>
      {hasHeader && (
        <div
          className={cn(
            'flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between',
            !flush && 'pb-0',
          )}
        >
          <div className="min-w-0">
            {title && (
              <Typography variant="subtitle1" component="h2" color="textHeading">
                {title}
              </Typography>
            )}
            {description && (
              <Typography variant="body2" color="textSecondary" className="mt-0.5">
                {description}
              </Typography>
            )}
          </div>
          {action && <div className="flex shrink-0 flex-wrap items-center gap-2">{action}</div>}
        </div>
      )}
      <div className={cn(!flush && 'p-4', hasHeader && !flush && 'pt-3', bodyClassName)}>
        {children}
      </div>
    </Card>
  );
}

export interface SelectableCardProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Omit this for a card that opens something. The card then has no pressed state. */
  selected?: boolean;
  lift?: CardProps['lift'];
}

/** Shows a card as a button. The card gets the selected style when `selected` is true. */
export function SelectableCard({ selected, lift, type = 'button', className, ...props }: SelectableCardProps) {
  return (
    <CardRoot
      component="button"
      type={type}
      aria-pressed={selected}
      ownerState={{ glow: Boolean(selected), lift: lift ?? 'none' }}
      className={cn('w-full text-left transition-shadow', className)}
      {...props}
    />
  );
}

/** Shows a thin rule between blocks, across or, with `vertical`, upright. */
export function Divider({ vertical = false, className }: { vertical?: boolean; className?: string }) {
  return <MuiDivider orientation={vertical ? 'vertical' : 'horizontal'} flexItem={vertical} className={className} />;
}

const ActionBarRoot = styled('div', { name: 'KitActionBar', slot: 'root' })({});

/** Shows the actions of a step. Below lg they stay at the bottom of the screen; from lg they are in the page flow. */
export function ActionBar({ children }: { children: ReactNode }) {
  return (
    <ActionBarRoot className="sticky bottom-0 z-10 -mx-4 flex flex-wrap items-center gap-3 px-4 py-3 sm:-mx-6 sm:px-6 lg:static lg:mx-0 lg:p-0">
      {children}
    </ActionBarRoot>
  );
}

/** Shows the sticky top bar of the app, with its content in one row. */
export function AppBar({ children }: { children: ReactNode }) {
  return (
    <MuiAppBar>
      <Toolbar disableGutters className="h-16 gap-3 px-4 sm:px-6">
        {children}
      </Toolbar>
    </MuiAppBar>
  );
}

export interface CollapsibleProps {
  title: string;
  /** Uncontrolled default. Pass `open`+`onOpenChange` to control it. */
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
}

/** Shows a card with a title button that opens and closes its panel. A closed panel is not mounted. */
export function Collapsible({
  title,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  actions,
  className,
  children,
}: CollapsibleProps) {
  const [open, setOpen] = useOpenState(controlledOpen, onOpenChange, defaultOpen);
  const panelId = useId();

  return (
    <Card className={className}>
      <div className="flex items-center gap-2 p-4">
        <ButtonBase
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls={panelId}
          className="flex flex-1 justify-start gap-2 text-left"
        >
          <ChevronDown size={ICON_SIZE.md} aria-hidden className={cn('shrink-0 transition-transform', open && 'rotate-180')} />
          <Typography component="span" variant="subtitle2" color="textHeading">
            {title}
          </Typography>
        </ButtonBase>
        {actions}
      </div>

      <Collapse in={open} unmountOnExit>
        <MuiDivider />
        <div id={panelId} className="p-4">
          {children}
        </div>
      </Collapse>
    </Card>
  );
}
